import React, { useEffect } from "react";
import { Building2, Church, Stethoscope, Users, Check, Mail } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { ReferralForm } from "../components/forms/ReferralForm";
import { ReferralStatusLookup } from "../components/ReferralStatusLookup";
import { SITE } from "../data/content";

const partnerTypes = [
  { icon: Users, label: "Case managers & social workers" },
  { icon: Church, label: "Churches & faith communities" },
  { icon: Stethoscope, label: "Hospitals & clinics" },
  { icon: Building2, label: "Agencies & community orgs" },
];

const steps = [
  { num: "01", title: "Submit the referral", body: "Share the veteran's basic details and situation using the form below. It takes just a few minutes." },
  { num: "02", title: "We review & respond", body: "Our intake team reviews the referral and reaches out — to you and, with permission, to the veteran." },
  { num: "03", title: "We take it from there", body: "We coordinate intake, build a stabilization plan, and keep you informed at appropriate checkpoints." },
];

const Partners = () => {
  useEffect(() => { document.title = "Refer a Veteran | Iron Shield Veterans Reset Center"; }, []);
  return (
    <>
      <PageHero
        eyebrow="Partners"
        lines={["Know a veteran", "who needs a reset?"]}
        intro="If you work with veterans — as a case manager, clinician, faith leader, or agency partner — this is the fastest way to connect someone with structured housing and support."
      />

      {/* Referral process */}
      <section className="py-20 md:py-28 bg-offwhite" data-testid="partners-process">
        <div className="container-x">
          <SectionHeading eyebrow="Referral process" title="Three steps. No red tape." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-y-10 md:gap-x-10">
            {steps.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1} className="relative pt-10">
                <span className="absolute -top-2 left-0 font-heading text-7xl font-black text-navy/10 leading-none">{s.num}</span>
                <div className="relative">
                  <div className="h-px w-full bg-softgray mb-6" />
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-navy">{s.title}</h3>
                  <p className="mt-3 text-charcoal/75 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Partner types */}
      <section className="py-16 md:py-20 bg-navy text-offwhite" data-testid="partners-types">
        <div className="container-x">
          <SectionHeading light eyebrow="Who this page is for" title="Partners we work with." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerTypes.map((p, i) => (
              <Reveal key={p.label} delay={i * 0.08} className="i-card-dark p-8">
                <p.icon className="h-6 w-6 text-gold" strokeWidth={1.75} />
                <div className="mt-5 font-semibold text-offwhite">{p.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Form + standards */}
      <section className="py-16 md:py-20 bg-white border-b border-softgray" data-testid="partners-form-section">
        <div className="container-x">
          <div className="max-w-3xl">
            <SectionHeading eyebrow="Referral form" title="Refer a veteran." />
            <div className="mt-10"><ReferralForm /></div>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
              <ReferralStatusLookup />
              <div className="i-card bg-offwhite p-8" data-testid="referral-standards">
                <div className="label-caps text-navy mb-4">Referral standards</div>
                <ul className="space-y-3 text-sm text-charcoal/80">
                  {["Confirm the veteran consents to being contacted", "Share only what's relevant and accurate", "Flag any immediate safety concerns clearly", "Provide a reachable contact for follow-up"].map((e) => (
                    <li key={e} className="flex items-start gap-3"><Check className="h-4 w-4 text-teal shrink-0 mt-0.5" /> <span>{e}</span></li>
                  ))}
                </ul>
              </div>
              <div className="i-card bg-offwhite p-8" data-testid="partner-inquiry">
                <div className="flex items-center gap-3 mb-4">
                  <Mail className="h-5 w-5 text-navy" />
                  <div className="label-caps text-navy">Partner inquiries</div>
                </div>
                <p className="text-sm text-charcoal/80 leading-relaxed">Interested in a formal partnership or a memorandum of understanding? Email us directly:</p>
                <a href={`mailto:${SITE.email}`} className="mt-3 inline-block font-semibold text-navy hover:text-gold-deep transition-colors" data-testid="partner-inquiry-email">{SITE.email}</a>
              </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Partners;
